import { useContext, useEffect, useState, useCallback } from "react"; // Added useCallback
import { useNavigate } from "react-router";

import UserContext from "../../context/userContext";
import { Loading } from "../Common/Loading";
// Assuming getDaypassByDate is exported from this service
import { getDaypass, postDaypass, getDaypassByDate, getMyDaypassByDate } from "../../services/daypass"; 
import { isSameDate } from "../util/util";

import {
  Button,
  useToast,
  Box,
  Text,
  UnorderedList, // Added
  ListItem,      // Added
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";

export const Amenidades = () => {
  const { t } = useTranslation();
  const toast = useToast();

  const { user } = useContext(UserContext); // Removed setUser as it's not used in the provided snippet
  const [ocupacion, setOcupacion] = useState(0);
  const [fechaSeleccionada, setFechaSeleccionada] = useState(new Date());
  const [isLoading, setIsLoading] = useState(true); // This will be set to false after initial load
  const [isDailyLoading, setIsDailyLoading] = useState(false); // For daily reservations list
  const [pases, setPases] = useState(0);

  const [daypass, setDaypass] = useState(null);
  const [dailyReservations, setDailyReservations] = useState([]);
  const [currentTotalCupo, setCurrentTotalCupo] = useState(50); // Fallback

  const [myDayPass, setMyDaypass] = useState(null); // Assuming this is needed later

  const navigate = useNavigate();

  const formatFecha = useCallback((date) => date.toISOString().split("T")[0], []);

  const handlePreviousDay = () => {
    const newDate = new Date(fechaSeleccionada);
    newDate.setDate(newDate.getDate() - 1);
    setFechaSeleccionada(newDate);
  };

  const handleNextDay = () => {
    const newDate = new Date(fechaSeleccionada);
    newDate.setDate(newDate.getDate() + 1);
    setFechaSeleccionada(newDate);
  };

  const handleReservarDaypass = async () => {
    if (pases <= 0 || pases > 6) { // Corrected condition: pases must be > 0
      toast({
        title: t("PASSES_LIMIT_WARNING", "El número de pases debe estar entre 1 y 6."),
        status: "error",
        position: "top",
        isClosable: true,
      });
      return;
    }
    await reserveDayPass();
  };

  const requestDaypass = useCallback(async () => {
    setIsLoading(true); // Set loading true at the start of the initial data fetch
    try {
      const response = await getDaypass();
      if (response.status === 200 && response.data) {
        setDaypass(response.data);
        if (response.data.totalCupo !== undefined) {
          setCurrentTotalCupo(response.data.totalCupo);
        }
      }
    } catch (error) {
      console.error("Error al obtener DayPass:", error);
      toast({
        title: t("DAYPASS_LOAD_ERROR", "Error al cargar datos generales de DayPass."),
        status: "error",
        position: "top",
        isClosable: true,
      });
    } finally {
      setIsLoading(false); // Set loading false after initial data fetch attempt
    }
  }, [toast, t]); // Added dependencies

  
  const reserveDayPass = async () => {
    const data = {
      fechaReservada: `${fechaSeleccionada.getFullYear()}-${(fechaSeleccionada.getMonth() + 1).toString().padStart(2, '0')}-${fechaSeleccionada.getDate().toString().padStart(2, '0')}`,
      numeroPersonas: pases,
    };
  
    setIsLoading(true);
  
    try {
      const response = await postDaypass(data);
  
      // ――― Éxito (HTTP 200) ―――
      if (response.status === 200) {
        // 1. Vuelve a traer el cupo/fechas globales
        await requestDaypass();
  
        // 2. Refresca solo la lista del día seleccionado
        await fetchDailyDaypassData(fechaSeleccionada);

        // 3. Refresca tu propio DayPass
        await requestMyDaypass();
  
        toast({
          title: t("DAYPASS_RESERVED_SUCCESS", "DayPass reservado con éxito."),
          status: "success",
          position: "top",
          isClosable: true,
        });
      } else {
        // Si viniera cualquier otro código, fuerza el error
        throw new Error("Unexpected response status");
      }
    } catch (error) {
      console.log("Error reservando DayPass:", error);
      const msg = error.response.data && error.response.data.error ? error.response.data.error :  t("DAYPASS_RESERVED_ERROR");
      toast({
        title: msg,
        status: "error",
        position: "top",
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  // Renamed to avoid conflict and for clarity
  const fetchDailyDaypassData = useCallback(async (dateToFetch) => {
    if (dateToFetch) {
        setIsDailyLoading(true);
        try {
            const formattedDate = `${dateToFetch.getFullYear()}-${(dateToFetch.getMonth() + 1).toString().padStart(2, '0')}-${dateToFetch.getDate().toString().padStart(2, '0')}`;
            const response = await getDaypassByDate(formattedDate);
            if (response && response.data) {
                let reservationsArray = []; // Default to empty array
                if (Array.isArray(response.data.reservations)) {
                    reservationsArray = response.data.reservations;
                } else if (Array.isArray(response.data)) {
                    reservationsArray = response.data;
                }
                setDailyReservations(reservationsArray);

                if (response.data.totalCupo !== undefined) { // Ensure this check is safe if response.data is an array
                    const cupoSource = Array.isArray(response.data) ? null : response.data; // totalCupo unlikely if response.data is array itself
                    if (cupoSource && cupoSource.totalCupo !== undefined) {
                       setCurrentTotalCupo(cupoSource.totalCupo);
                    }
                }
            } else {
                setDailyReservations([]); // Ensure it's an array on no/empty data
            }
        } catch (error) {
            console.error("Error fetching daily daypass reservations:", error);
            toast({
                title: t("DAILY_DAYPASS_LOAD_ERROR", "Error al cargar reservaciones del día."),
                status: "error",
                position: "top",
                isClosable: true,
            });
            setDailyReservations([]);
        } finally {
            setIsDailyLoading(false);
        }
    }
  }, [formatFecha, toast, t]); // Added dependencies

  const requestMyDaypass = async () => {
    try {
      const formattedDate = `${fechaSeleccionada.getFullYear()}-${(fechaSeleccionada.getMonth() + 1).toString().padStart(2, '0')}-${fechaSeleccionada.getDate().toString().padStart(2, '0')}`;
      const response = await getMyDaypassByDate(formattedDate);
      setMyDaypass(response.data);
    } catch (error) {
      console.error("Error al obtener mi DayPass:", error);
      toast({
        title: t("MY_DAYPASS_LOAD_ERROR", "Error al cargar tu DayPass."),
        status: "error",
        position: "top",
        isClosable: true,
      });
    }
  }

  useEffect(() => {
    requestDaypass();
  }, [requestDaypass]);

  useEffect(() => {
    requestMyDaypass();
  }, [fechaSeleccionada]);

  useEffect(() => {
    fetchDailyDaypassData(fechaSeleccionada);
  }, [fechaSeleccionada, fetchDailyDaypassData]);


  useEffect(() => {
    if (!daypass || !daypass.fechasCupo || daypass.fechasCupo.length === 0) {
      setOcupacion(0);
      return;
    }
    const reservasEnFecha = daypass.fechasCupo.filter((reserva) => {
      const splitDate = reserva.fecha.split("T")[0].split("-");
      const reservaFecha = new Date(
        splitDate[0],
        splitDate[1] - 1,
        splitDate[2]
      );
      return isSameDate(reservaFecha, fechaSeleccionada);
    });
    const totalReservados = reservasEnFecha.reduce(
      (acc, curr) => acc + (curr.personasDentro || 0),
      0
    );
    // Use currentTotalCupo for percentage calculation
    const porcentaje = currentTotalCupo > 0 ? Math.round((totalReservados / currentTotalCupo) * 100) : 0;
    setOcupacion(porcentaje);
  }, [fechaSeleccionada, daypass, currentTotalCupo]);

  if (isLoading && !daypass) return <Loading />; // Show loading only on initial full load

  const disabledReserva = ocupacion >= 100 || pases <= 0; // Also disable if pases is 0 or less

  return (
    <Box mt={{ base: 2, lg: 3 }} mx={{ base: 2, lg: 5 }}>
      <Text className="section-title">{t("RESERVE_YOUR_DAYPASS", "Reserva tu DayPass")}</Text>

      <Box textAlign="center" mb={5}>
        <Box
          w="200px"
          h="200px"
          borderRadius="full"
          bg={`conic-gradient(#e06c00 ${ocupacion}%, #f2f2f2 ${ocupacion}% 100%)`}
          m="auto"
          display="flex"
          alignItems="center"
          justifyContent="center"
          fontWeight="bold"
          fontSize="24px"
          color="#333"
        >
          {ocupacion >= 100 ? t("FULL", "LLENO") : `${ocupacion}%`}
        </Box>
        <Text mt={2} fontWeight="bold">
          {t("AMENITIES_AVAILABILITY", "Disponibilidad de Amenidades")}
        </Text>
      </Box>

      <Box display="flex" justifyContent="center" mt={5}>
        <Button
          bg="#e06c00"
          color="white"
          _hover={{ bg: "#c45a00" }}
          onClick={handlePreviousDay}
        >
          ⟵ {t("PREVIOUS_DAY", "Día Anterior")}
        </Button>
        <Box mx={3} alignSelf="center" fontWeight="medium">
          {`${fechaSeleccionada.getFullYear()}-${(fechaSeleccionada.getMonth() + 1).toString().padStart(2, '0')}-${fechaSeleccionada.getDate().toString().padStart(2, '0')}`}
        </Box>
        <Button
          bg="#e06c00"
          color="white"
          _hover={{ bg: "#c45a00" }}
          onClick={handleNextDay}
        >
          {t("NEXT_DAY", "Día Siguiente")} ⟶
        </Button>
      </Box>

      <Box textAlign="center" mt={8}>
        <Box mb={3}>
          <label style={{ marginRight: "8px" }}>{t("DAYPASS_PASSES_LABEL", "Número de Pases:")}</label>
          <input
            type="number"
            min="1" // Min should be 1 if pases <= 0 is disabled
            max="6"
            value={pases}
            onChange={(e) => setPases(parseInt(e.target.value) || 0)}
            style={{ width: "60px", padding: "5px", borderRadius: "5px", border: "1px solid #ccc" }}
          />
        </Box>
        <Button
          bg="#e06c00"
          color="white"
          _hover={{ bg: "#c45a00" }}
          onClick={handleReservarDaypass}
          disabled={disabledReserva}
          isLoading={isLoading} // Show loading on this button during reservation
        >
          {disabledReserva && ocupacion >=100 ? t("NO_SPACE_LEFT", "SIN CUPO") : t("RESERVE_DAYPASS", "Reservar DayPass")}
        </Button>
      </Box>

      {/* Lista de Reservaciones DayPass para la fecha seleccionada */}
      <Box mt={8} px={{ base: 2, lg: 0 }}>
        <Text fontSize="xl" fontWeight="bold" mb={4}>
          {t("DAYPASS_RESERVATIONS_FOR_DATE", `Reservaciones para el ${fechaSeleccionada.getFullYear()}-${(fechaSeleccionada.getMonth() + 1).toString().padStart(2, '0')}-${fechaSeleccionada.getDate().toString().padStart(2, '0')}`)}
        </Text>
        {isDailyLoading ? (
            <Loading />
        ) : !Array.isArray(myDayPass) || myDayPass.length === 0 ? (
          <Text>{t("NO_DAYPASS_RESERVATIONS_FOR_DATE", "No hay reservaciones de DayPass para esta fecha.")}</Text>
        ) : (
          <UnorderedList spacing={3}>
            {myDayPass.map((res, index) => (
              <ListItem key={index}>
                {t("RESERVATION_FOR", "Reservación para")} {res.numPersonas} {t("PEOPLE", "personas")} - {formatFecha(new Date(res.fechaReservada))}
              </ListItem>
            ))}
          </UnorderedList>
        )}
      </Box>
    </Box>
  );
};

export default Amenidades;